/**
 * Auth Mode Toggle
 *
 * Tab-style buttons for switching between login and register modes
 * in the authentication component.
 */

import { useTranslation } from "../../hooks/useTranslation";
import type { AuthMode } from "./types";

export interface AuthModeToggleProps {
  /** Current authentication mode */
  mode: AuthMode;
  /** Callback when mode is changed */
  onModeChange: (mode: AuthMode) => void;
}

/**
 * Toggle buttons for switching between Sign In and Register views
 *
 * @example
 * ```tsx
 * <AuthModeToggle
 *   mode="login"
 *   onModeChange={(newMode) => setMode(newMode)}
 * />
 * ```
 */
export function AuthModeToggle({ mode, onModeChange }: AuthModeToggleProps) {
  const t = useTranslation();

  return (
    <div className="flex justify-center gap-2 mt-6">
      <button
        type="button"
        className={`px-4 py-2 rounded-lg text-sm ${
          mode === "login"
            ? "bg-red-600 text-white"
            : "bg-gray-100 text-gray-700"
        }`}
        onClick={() => onModeChange("login")}
      >
        {t("signIn")}
      </button>
      <button
        type="button"
        className={`px-4 py-2 rounded-lg text-sm ${
          mode === "register"
            ? "bg-red-600 text-white"
            : "bg-gray-100 text-gray-700"
        }`}
        onClick={() => onModeChange("register")}
      >
        {t("register")}
      </button>
    </div>
  );
}
